/**
 * Sentry error-suppression filter.
 *
 * Each entry is a RegExp (or plain string) matched by Sentry against an error's message; any error
 * that matches is dropped and never reported. This list is wired into the `ignoreErrors` option of
 * the `Sentry.init(...)` call in `src/index.js`, and `mergeSentryConfig` concatenates it onto the
 * org-wide defaults from `@fs/zion-config`.
 *
 * Use this ONLY for errors that are outside dmconsole's control and are not actionable for our
 * team — third-party/library noise or browser quirks that we cannot fix here — never to hide a
 * real application bug we should be tracking.
 *
 * To suppress a new error: add a RegExp (or string) below with a comment linking the ticket that
 * justifies it.
 */
const sentryIgnoredErrors = [
  // Browser-level DOMException thrown by Chrome when a File/Blob's backing file is moved or deleted
  // before it can be read. Not caused or fixable by application code (there is no file/blob reading
  // in the pages where it surfaces). See DATA-12254.
  /NotReadableError/,
  /Data lost due to missing file/,
  // Thrown by Adobe's alloy Web SDK ("[alloy] [ActivityCollector]") during its automatic link-click
  // tracking: the externally-managed Adobe Launch property's onBeforeEventSend still calls the
  // legacy AppMeasurement/DTM `_satellite.doPlugins`, which does not exist under the alloy (Web SDK)
  // runtime. The clicked link still opens correctly; this is non-actionable analytics noise owned
  // by the Adobe Launch / @fs/zion-analytics platform, not dmconsole. See DATA-12257; the platform
  // defect to fix it at the source is DATA-12353.
  /_satellite\.doPlugins is not a function/,
  // Thrown inside localforage's IndexedDB driver (`_tryReconnect`), which dereferences
  // `forage._dbInfo.db` without a null check for every forage instance sharing a database name.
  // When one instance hits an InvalidStateError/NotFoundError opening a transaction (the browser
  // silently dropped the IndexedDB connection) while a sibling instance is mid-`setDriver` driver
  // negotiation -- where localforage itself resets `_dbInfo` to null -- the reconnect loop throws.
  // dmconsole never imports localforage; it arrives transitively via @fs/zion-cache (which
  // registers extra memoryStorage/sessionStorage drivers, making those null windows reachable) and
  // @sentry/integrations. localforage 1.10.0 is the latest published release, so there is no
  // upgrade to take and nothing to guard from application code. See DATA-12265.
  // NOTE: only the phrasings that name localforage's internal `_dbInfo` are matched. Chrome renders
  // this same fault as the generic "Cannot read properties of null (reading 'db')", which is
  // deliberately NOT ignored here -- it is too broad and would mask real application bugs.
  /_dbInfo is null/,
  /_dbInfo\.db/,
  // WebKit/Mobile Safari deletes the site's IndexedDB database out from under a transitive
  // dependency's open connection (private-browsing storage eviction or similar); dmconsole's own
  // code never touches IndexedDB. Surfaces via an unhandled promise rejection with no stacktrace.
  // See DATA-12515.
  /Database deleted by request of the user/,
  // Chrome can't open a transitive dependency's IndexedDB because the user's disk is full. See DATA-12626.
  /Encountered full disk while opening backing store/,
  // WebKit aborts a dependency's in-flight fetch when the page is torn down; dmconsole never aborts a request itself.
  /AbortError: The operation was aborted/,
]

export default sentryIgnoredErrors
